import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Zap } from "lucide-react";
import Seo from "../components/Seo";
import api, { apiErr, track } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { money, currencyFor } from "../lib/money";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";

export default function Pricing() {
  const { locale, t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [cat, setCat] = useState({ plans: [], packs: [] });
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/billing/catalog").then(({ data }) => setCat(data)).catch(() => {});
  }, []);

  const checkout = async (lookupKey) => {
    if (!user) {
      nav(`/${locale}/register`);
      return;
    }
    setBusy(lookupKey);
    try {
      track("checkout_started", { lookup_key: lookupKey });
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        currency: currencyFor(locale),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(null);
    }
  };

  return (
    <div className="pt-16" data-testid="pricing-page">
      <Seo
        title={locale === "fr" ? "Tarifs — Studio Movies IA" : "Pricing — Studio Movies IA"}
        description={t("pricing.sub")}
        locale={locale}
        path={`/${locale}/pricing`}
        altPath={`/${locale === "en" ? "fr" : "en"}/pricing`}
      />

      <section className="px-6 pb-16 pt-20 lg:px-10 lg:pt-28">
        <div className="mx-auto max-w-[1400px]">
          <p className="overline">Movies Credits</p>
          <h1 className="mt-4 max-w-[820px] font-display text-4xl font-semibold sm:text-5xl lg:text-6xl">
            {t("pricing.title")}
          </h1>
          <p className="mt-6 max-w-[620px] text-base text-[#9194a6] md:text-lg">{t("pricing.sub")}</p>

          <div className="mt-10 inline-flex rounded-full border border-[#2a2c3c] p-1">
            {[[false, t("pricing.monthly")], [true, t("pricing.yearly")]].map(([v, label]) => (
              <button
                key={label}
                onClick={() => setYearly(v)}
                data-testid={`billing-toggle-${v ? "yearly" : "monthly"}`}
                className={`rounded-full px-5 py-2 text-[13px] font-semibold transition-colors duration-200 ${
                  yearly === v ? "bg-white text-black" : "text-[#9194a6] hover:text-white"
                }`}
              >
                {label}
                {v && <span className="mono ml-2 text-[10px] text-[#ff4b2b]">-17%</span>}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-24 lg:px-10">
        <div className="mx-auto grid max-w-[1400px] gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {cat.plans.map((p) => {
            const price = yearly ? p.price_yearly : p.price_monthly;
            const lk = yearly ? p.lookup_key_yearly : p.lookup_key_monthly;
            const isEnterprise = p.code === "enterprise";
            const isCurrent = user?.plan === p.code;
            return (
              <div
                key={p.code}
                data-testid={`plan-card-${p.code}`}
                className={`hot-border relative flex flex-col rounded-2xl border p-7 ${
                  p.popular ? "border-[#ff4b2b]/45 bg-[#ff4b2b]/[0.05]" : "border-[#212230] bg-[#0b0b11]"
                }`}
              >
                {p.popular && (
                  <span className="mono absolute -top-2.5 left-7 rounded-full bg-[#ff4b2b] px-2.5 py-0.5 text-[9px] uppercase tracking-[0.14em] text-white">
                    {t("pricing.popular")}
                  </span>
                )}
                <p className="font-display text-lg font-medium">{p.name}</p>
                <div className="mt-5 flex items-end gap-1.5">
                  {isEnterprise ? (
                    <span className="font-display text-3xl font-semibold">
                      {locale === "fr" ? "Sur devis" : "Custom"}
                    </span>
                  ) : (
                    <>
                      <span className="font-display text-4xl font-semibold">
                        {money(price, locale)}
                      </span>
                      <span className="mono mb-1.5 text-[11px] text-[#9194a6]">
                        /{yearly ? (locale === "fr" ? "an" : "yr") : locale === "fr" ? "mois" : "mo"}
                      </span>
                    </>
                  )}
                </div>
                <p className="mono mt-3 flex items-center gap-1.5 text-[11px] text-[#00d5ff]">
                  <Zap className="h-3 w-3" />
                  {p.credits_monthly.toLocaleString()} credits
                  {p.code === "free"
                    ? (locale === "fr" ? ", une seule fois" : ", one time")
                    : (locale === "fr" ? "/mois" : "/mo")}
                </p>

                <ul className="mt-7 flex-1 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-[13px] text-[#c0c3d0]">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#00ff9d]" />
                      {f}
                    </li>
                  ))}
                </ul>

                {isEnterprise ? (
                  <Link
                    to={`/${locale}/contact`}
                    className="btn-ghost mt-8 rounded-full py-3 text-center text-[13px] font-semibold"
                  >
                    {t("pricing.contact")}
                  </Link>
                ) : p.code === "free" ? (
                  <Link
                    to={user ? "/studio" : `/${locale}/register`}
                    data-testid="plan-cta-free"
                    className="btn-ghost mt-8 rounded-full py-3 text-center text-[13px] font-semibold"
                  >
                    {user ? t("nav.studio") : t("nav.start")}
                  </Link>
                ) : (
                  <button
                    onClick={() => checkout(lk)}
                    disabled={busy === lk || isCurrent}
                    data-testid={`plan-cta-${p.code}`}
                    className={`mt-8 rounded-full py-3 text-[13px] font-semibold disabled:opacity-50 ${
                      p.popular ? "btn-amber" : "btn-primary"
                    }`}
                  >
                    {isCurrent ? t("pricing.current") : busy === lk ? "…" : t("pricing.choose")}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-[#08080d] px-6 py-24 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <p className="overline">{t("pricing.packs")}</p>
          <h2 className="mt-4 font-display text-3xl font-semibold sm:text-4xl">
            {locale === "fr" ? "Des crédits qui n'expirent jamais" : "Credits that never expire"}
          </h2>
          <p className="mt-5 max-w-[600px] text-base text-[#9194a6]">
            {locale === "fr"
              ? "À court de crédits en pleine production ? Ajoutez un pack sans changer d'abonnement. Activez l'auto-recharge dans le Studio pour ne jamais interrompre un rendu."
              : "Out of credits mid-production? Add a pack without changing your subscription. Turn on auto-recharge in the Studio so a render never stops."}
          </p>

          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {cat.packs.map((p) => (
              <div
                key={p.code}
                data-testid={`pack-card-${p.code}`}
                className={`hot-border flex items-center justify-between rounded-xl border p-5 ${
                  p.featured ? "border-[#00d5ff]/35 bg-[#00d5ff]/[0.04]" : "border-[#212230] bg-[#0b0b11]"
                }`}
              >
                <div>
                  <p className="font-display text-sm font-medium">{p.name}</p>
                  <p className="mono mt-1.5 text-[11px] text-[#9194a6]">
                    {money(p.price, locale, 2)}
                  </p>
                </div>
                <button
                  onClick={() => checkout(p.lookup_key)}
                  disabled={busy === p.lookup_key}
                  data-testid={`pack-cta-${p.code}`}
                  className="btn-ghost shrink-0 rounded-full px-4 py-2 text-[12px] font-semibold disabled:opacity-50"
                >
                  {busy === p.lookup_key ? "…" : t("pricing.buy")}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-16 grid gap-5 rounded-2xl border border-[#212230] bg-[#0b0b11] p-8 sm:grid-cols-3">
            {[
              [locale === "fr" ? "Image / storyboard" : "Image / storyboard", "10"],
              [locale === "fr" ? "Plan 5 s standard" : "5s standard shot", "70"],
              [locale === "fr" ? "Plan 5 s Cinema (audio natif)" : "5s Cinema shot (native audio)", "365"],
            ].map(([label, credits]) => (
              <div key={label}>
                <p className="mono text-[11px] uppercase tracking-[0.16em] text-[#8288a0]">{label}</p>
                <p className="mt-2 font-display text-2xl font-semibold text-[#ff4b2b]">
                  {credits} <span className="text-sm text-[#9194a6]">credits</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-24 text-center lg:px-10">
        <h2 className="mx-auto max-w-[700px] font-display text-3xl font-semibold sm:text-4xl">
          {t("cta.final")}
        </h2>
        <Link
          to={user ? "/studio" : `/${locale}/register`}
          className="btn-amber mt-9 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold"
        >
          {t("hero.cta1")} <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </div>
  );
}
